import { createRouter, createWebHistory } from 'vue-router';
import { i18n, ALL_LANGS } from './i18n';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import TableOfContents from '../components/TableOfContents';
import Home from '../pages/Home';
import Page from '../pages/Page';
import Feedback from '../pages/Feedback';
import Product from '../pages/Product';
import Purchase from '../pages/Purchase';
import PurchaseHistory from '../pages/PurchaseHistory';
import MyNotifications from '../pages/MyNotifications';
import Faq from '../pages/Faq';
import Contacts from '../pages/Contacts';
import Login from '../pages/Login';
import LoginFake from '../pages/LoginFake';
import Prices from '../pages/Prices';
import Profile from '../pages/Profile';
import DisputeForm from '../pages/DisputeForm';
import DisputeInfo from '../pages/DisputeInfo';
import ROUTE_PATHS from '../lang/routes.yml';
import MyConsents from '@/pages/MyConsents.vue';
import MyConsentRequests from '@/pages/MyConsentRequests.vue';
import PurchaseConfirmation from '../pages/PurchaseConfirmation';

const routes = [
  {
    name: 'home',
    component: Home,
    meta: { layout: 'router-view' },
  },
  {
    name: 'login',
    component: Login,
    beforeEnter(to, from, next) {
      if (import.meta.env.VITE_IS_AUTHENTICATION_INTEGRATION_FAKED === 'true') {
        let lang = to.params.lang;
        next({ path: `/${lang}/login_fake` });
        return;
      }
      next();
    },
  },
  {
    name: 'dispute',
    component: DisputeForm,
    props: true,
  },
  {
    name: 'disputePrivate',
    component: DisputeForm,
    props: { default: true, isPrivate: true },
  },
  {
    name: 'disputeInfo',
    component: DisputeInfo,
    props: () => ({
      isPrivate: !!window.history.state.isPrivate,
    }),
  },
  {
    name: 'login_fake',
    component: LoginFake,
    beforeEnter(to, from, next) {
      if (import.meta.env.VITE_IS_AUTHENTICATION_INTEGRATION_FAKED !== 'true') {
        next({ path: '/login' });
        return;
      }
      next();
    },
  },
  {
    name: 'report',
    component: Product,
    props: true,
    meta: { layout: 'router-view' },
  },
  {
    name: 'aboutus',
    components: {
      default: Page,
      sidebar: Feedback,
    },
    props: {
      default: { id: 'about' },
    },
    meta: { layout: LayoutWithSidebar },
  },
  {
    name: 'faq',
    components: {
      default: Faq,
      sidebar: Feedback,
    },
    props: {
      default: {
        id: 'faq',
        contentClass: 'with-toc',
      },
    },
    meta: {
      layout: LayoutWithSidebar,
      layoutProps: {
        sidebarClass: '',
      },
    },
  },
  {
    name: 'contacts',
    components: {
      default: Contacts,
      sidebar: Feedback,
    },
    meta: {
      layout: LayoutWithSidebar,
      layoutProps: {
        sidebarClass: '',
      },
    },
  },
  {
    name: 'terms',
    components: {
      default: Page,
      sidebar: TableOfContents,
    },
    props: {
      default: {
        id: 'usage-terms',
        contentClass: 'with-toc',
      },
    },
    meta: {
      layout: LayoutWithSidebar,
      layoutProps: {
        sidebarClass: 'd-none d-lg-block',
      },
    },
  },
  {
    name: 'purchase',
    component: Purchase,
    meta: { navbarType: 'simple' },
  },
  {
    name: 'purchaseHistory',
    components: {
      default: PurchaseHistory,
    },
  },
  {
    name: 'purchaseConfirmation',
    components: {
      default: PurchaseConfirmation,
    },
  },
  {
    name: 'myNotifications',
    components: {
      default: MyNotifications,
    },
  },
  {
    name: 'myConsents',
    components: {
      default: MyConsents,
    },
  },
  {
    name: 'myConsentRequests',
    components: {
      default: MyConsentRequests,
    },
  },
  {
    name: 'prices',
    component: Prices,
  },
  {
    name: 'profile',
    components: {
      default: Profile,
    },
  },
  {
    path: '/:lang/search-companies',
    name: 'searchCompanies',
    beforeEnter(to, from, next) {
      window.location.href = import.meta.env.VITE_SEARCH_COMPANY_URL
        .replace(/\?.+$/, '');
      next(false);
    },
  },
  {
    path: '/:lang/*',
    redirect: (to) => `/${to.params.lang || i18n.locale}`,
  },
];

export const generateLocalizedRoutes = () => routes.flatMap((route) => {
  if (route.path) return [route];

  return ALL_LANGS.map((lang) => ({
    ...route,
    path: `/:lang${ROUTE_PATHS[lang][route.name] || ''}`,
    name: `${route.name}-${lang}`,
    meta: { ...route.meta, lang },
  }));
});

export const router = createRouter({
  i18n,
  lang: i18n.global.locale.value,
  langs: ALL_LANGS,
  history: createWebHistory(),
  scrollBehavior(to) {
    if (to.hash) {
      return {
        selector: to.hash,
        offset: { x: 0, y: 100 },
      };
    }

    return { x: 0, y: 0 };
  },
  routes: generateLocalizedRoutes(routes),
});

router.beforeEach((to, from, next) => {
  const targetLang = to.params.lang;
  const pathLang = to.path.split('/')[1];

  if (targetLang && ALL_LANGS.includes(targetLang)) {
    i18n.global.locale.value = targetLang;
  }

  if (to.path.includes('search-companies')) {
    window.location.href = import.meta.env.VITE_SEARCH_COMPANY_URL
      .replace(/\?.+$/, '');
    next(false);
  }

  if (!ALL_LANGS.includes(pathLang)) {
    const selectedLang = to.meta.lang || i18n.global.locale.value;
    const newPath = `/${selectedLang}${to.path}`;
    next({ path: newPath, replace: true, params: { lang: selectedLang } });
  } else {
    next();
  }
});
